import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, type ButtonVariant } from '@televet/kibble-ui/Button';
import { Card } from '@televet/kibble-ui/Card';
import { Divider } from '@televet/kibble-ui/Divider';
import { Heading } from '@televet/kibble-ui/Heading';
import { Icon } from '@televet/kibble-ui/Icon';
import { Select } from '@televet/kibble-ui/Select';
import { Spinner } from '@televet/kibble-ui/Spinner';
import { Tag } from '@televet/kibble-ui/Tag';
import { Text } from '@televet/kibble-ui/Text';
import { TextInput } from '@televet/kibble-ui/TextInput';
import { Flex, Table } from '@televet/kibble-ui/chakra';
import { Controller, useForm } from 'react-hook-form';
import { trpc } from '@/lib/trpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageContainer } from '@/components/PageContainer';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { CodeEditor } from '@/components/CodeEditor';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { toaster } from '@/components/ui/toaster';
import { parameterObject, validateParameters } from '@/lib/parameterSchema';
import type { DataSourceNamespace } from '@/lib/dataSourceNamespace';
import { useState } from 'react';
import { InstanceParameterEditor } from './-components/InstanceParameterEditor';
import { InstanceChainLimits } from './-components/InstanceChainLimits';
import { chunkLimitsLock } from './-lib/chunkLimits';
import { parseNumberInput } from './-lib/parseNumberInput';
import { formatDuration } from './-lib/executionConstants';
import { EXECUTION_MODE_TAG_VARIANT } from './executions/-lib/records';
import { ExecutionStatusTag, InstanceStatusTag } from './-components/StatusTag';

export const Route = createFileRoute('/_authenticated/emitters/$instanceId')({
  component: InstanceDetail,
});

interface InstanceFormValues {
  name: string;
  parameters: string;
  scheduleType: string;
  scheduleCron: string;
  dedupeStrategy: string;
  dedupeWindowExecutions: string;
  dedupeWindowDays: string;
  dedupeWindowHours: string;
  dataSourceNamespace: string;
  queryTimeoutSeconds: string;
  staticData: string;
}

const SCHEDULE_TYPES = [
  { value: 'Cron', label: 'Cron' },
  { value: 'Once', label: 'Once' },
  { value: 'Manual', label: 'Manual' },
];

const DEDUPE_STRATEGIES = [
  { value: 'None', label: 'None' },
  { value: 'GlobalLifetime', label: 'Global Lifetime' },
  { value: 'RollingWindow', label: 'Rolling Window' },
  { value: 'TimeWindow', label: 'Time Window' },
];

const DATA_SOURCE_NAMESPACES = [
  { value: '', label: 'Inherit template default' },
  { value: 'Owl', label: 'Owl (us-east-2)' },
  { value: 'Yak', label: 'Yak (us-west-2)' },
];

const EXECUTION_MODES: Array<{
  value: string;
  label: string;
  description: string;
  buttonVariant: ButtonVariant;
}> = [
  {
    value: 'Full',
    label: 'Full',
    description: 'Persist results + emit events',
    buttonVariant: 'primaryDestructive',
  },
  {
    value: 'PersistOnly',
    label: 'Persist Only',
    description: 'Persist results, skip events',
    buttonVariant: 'primaryWarning',
  },
  {
    value: 'DryRun',
    label: 'Dry Run',
    description: 'Preview only — no persist, no events',
    buttonVariant: 'primary',
  },
];

// --- Diff comparison result types ---

interface DiffResult {
  dataSourceNamespace: DataSourceNamespace | null;
  current: {
    parameters: Record<string, unknown>;
    results: Record<string, unknown>[];
    rowCount: number;
    durationMs: number;
  };
  proposed: {
    parameters: Record<string, unknown>;
    results: Record<string, unknown>[];
    rowCount: number;
    durationMs: number;
  };
  diff: {
    added: number;
    removed: number;
    unchanged: number;
    hasIdentityColumns: boolean;
    currentTotal: number;
    proposedTotal: number;
  };
}

// --- Parameter Test Panel ---

function ParameterTestPanel({
  instanceId,
  currentParametersJson,
}: {
  instanceId: string;
  currentParametersJson: string;
}) {
  const [proposedParams, setProposedParams] = useState(currentParametersJson);
  const [diffResult, setDiffResult] = useState<DiffResult | null>(null);
  const [activeTab, setActiveTab] = useState<'diff' | 'current' | 'proposed'>('diff');

  const { mutate: testParams, isPending: isTesting } = useMutation(
    trpc.emitter.testInstanceParameters.mutationOptions({
      onSuccess: (data) => {
        setDiffResult(data as DiffResult);
        toaster.success({ title: 'Comparison complete' });
      },
      onError: (error) => {
        toaster.error({
          title: 'Test failed',
          description: error.message,
        });
      },
    })
  );

  const runComparison = () => {
    try {
      const parsed = JSON.parse(proposedParams);
      testParams({
        instanceId,
        parameters: parsed,
        limit: 25,
      });
    } catch {
      toaster.error({
        title: 'Invalid JSON',
        description: 'Please check your parameter JSON syntax.',
      });
    }
  };

  return (
    <Card>
      <Flex direction="column" gap={4}>
        <Flex justify="space-between" align="center">
          <Text fontWeight="bold">Test Parameter Changes</Text>
          <Button size="sm" onClick={runComparison} loading={isTesting}>
            Run Comparison
          </Button>
        </Flex>
        <Text fontSize="sm" variant="subtle">
          Edit the proposed parameters below and run a comparison to see how results differ from the
          current saved parameters. No data is persisted or events emitted.
        </Text>
        <CodeEditor
          value={proposedParams}
          onChange={(v) => setProposedParams(v)}
          language="json"
          minHeight="120px"
        />

        {diffResult && (
          <>
            <Divider />
            <DiffSummary
              diff={diffResult.diff}
              dataSourceNamespace={diffResult.dataSourceNamespace}
            />

            <Flex gap={2}>
              {(['diff', 'current', 'proposed'] as const).map((tab) => (
                <Button
                  key={tab}
                  size="xs"
                  variant={activeTab === tab ? 'primary' : 'secondary'}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === 'diff'
                    ? 'Summary'
                    : tab === 'current'
                      ? `Current (${diffResult.current.rowCount})`
                      : `Proposed (${diffResult.proposed.rowCount})`}
                </Button>
              ))}
            </Flex>

            {activeTab === 'diff' && <DiffDetails diff={diffResult} />}
            {activeTab === 'current' && (
              <ResultsTable
                results={diffResult.current.results}
                durationMs={diffResult.current.durationMs}
              />
            )}
            {activeTab === 'proposed' && (
              <ResultsTable
                results={diffResult.proposed.results}
                durationMs={diffResult.proposed.durationMs}
              />
            )}
          </>
        )}
      </Flex>
    </Card>
  );
}

function DiffSummary({
  diff,
  dataSourceNamespace,
}: {
  diff: DiffResult['diff'];
  dataSourceNamespace: DataSourceNamespace | null;
}) {
  return (
    <Flex gap={4} wrap="wrap" align="center">
      {dataSourceNamespace && (
        <Tag size="sm" variant="info" label={`${dataSourceNamespace} data`} />
      )}
      <Flex direction="column" align="center" p={3} bg="bg.muted" rounded="md" minW="100px">
        <Text fontSize="2xl" fontWeight="bold">
          {diff.currentTotal}
        </Text>
        <Text fontSize="xs" variant="subtle">
          Current rows
        </Text>
      </Flex>
      <Flex direction="column" align="center" p={3} bg="bg.muted" rounded="md" minW="100px">
        <Text fontSize="2xl" fontWeight="bold">
          {diff.proposedTotal}
        </Text>
        <Text fontSize="xs" variant="subtle">
          Proposed rows
        </Text>
      </Flex>
      {diff.hasIdentityColumns && (
        <>
          <Flex
            direction="column"
            align="center"
            p={3}
            bg="background.success"
            rounded="md"
            minW="100px"
          >
            <Text fontSize="2xl" fontWeight="bold" variant="success">
              +{diff.added}
            </Text>
            <Text fontSize="xs" variant="subtle">
              Added
            </Text>
          </Flex>
          <Flex
            direction="column"
            align="center"
            p={3}
            bg="background.danger"
            rounded="md"
            minW="100px"
          >
            <Text fontSize="2xl" fontWeight="bold" variant="danger">
              -{diff.removed}
            </Text>
            <Text fontSize="xs" variant="subtle">
              Removed
            </Text>
          </Flex>
          <Flex direction="column" align="center" p={3} bg="bg.muted" rounded="md" minW="100px">
            <Text fontSize="2xl" fontWeight="bold">
              {diff.unchanged}
            </Text>
            <Text fontSize="xs" variant="subtle">
              Unchanged
            </Text>
          </Flex>
        </>
      )}
      {!diff.hasIdentityColumns && (
        <Flex align="center" p={3}>
          <Text fontSize="sm" variant="subtle">
            No identity columns configured — row-level diff unavailable. Comparing row counts only.
          </Text>
        </Flex>
      )}
    </Flex>
  );
}

function DiffDetails({ diff }: { diff: DiffResult }) {
  const delta = diff.proposed.rowCount - diff.current.rowCount;
  const deltaLabel = delta > 0 ? `+${delta}` : delta < 0 ? `${delta}` : 'no change';
  const deltaVariant = delta > 0 ? 'success' : delta < 0 ? 'danger' : 'subtle';

  return (
    <Flex direction="column" gap={2}>
      <Flex gap={8}>
        <Flex direction="column" gap={1}>
          <Text fontWeight="medium" fontSize="sm">
            Row count change
          </Text>
          <Text fontSize="lg" fontWeight="bold" variant={deltaVariant}>
            {deltaLabel}
          </Text>
        </Flex>
        <Flex direction="column" gap={1}>
          <Text fontWeight="medium" fontSize="sm">
            Current query time
          </Text>
          <Text fontSize="sm" fontFamily="mono">
            {diff.current.durationMs}ms
          </Text>
        </Flex>
        <Flex direction="column" gap={1}>
          <Text fontWeight="medium" fontSize="sm">
            Proposed query time
          </Text>
          <Text fontSize="sm" fontFamily="mono">
            {diff.proposed.durationMs}ms
          </Text>
        </Flex>
      </Flex>

      <Text fontWeight="medium" fontSize="sm" mt={2}>
        Parameter diff
      </Text>
      <Flex gap={4}>
        <Flex direction="column" flex={1} gap={1}>
          <Tag size="sm" variant="default" label="Current" />
          <CodeEditor
            value={JSON.stringify(diff.current.parameters, null, 2)}
            onChange={() => {}}
            language="json"
            readOnly
            minHeight="80px"
          />
        </Flex>
        <Flex direction="column" flex={1} gap={1}>
          <Tag size="sm" variant="info" label="Proposed" />
          <CodeEditor
            value={JSON.stringify(diff.proposed.parameters, null, 2)}
            onChange={() => {}}
            language="json"
            readOnly
            minHeight="80px"
          />
        </Flex>
      </Flex>
    </Flex>
  );
}

function ResultsTable({
  results,
  durationMs,
}: {
  results: Record<string, unknown>[];
  durationMs: number;
}) {
  if (results.length === 0) {
    return (
      <Text fontSize="sm" variant="subtle">
        No results returned.
      </Text>
    );
  }

  const columns = Object.keys(results[0]);

  return (
    <Flex direction="column" gap={2}>
      <Text fontSize="xs" variant="subtle">
        {results.length} rows in {durationMs}ms (capped at 25)
      </Text>
      <Flex overflow="auto" maxH="400px">
        <Table.Root size="sm" variant="outline">
          <Table.Header>
            <Table.Row>
              {columns.map((col) => (
                <Table.ColumnHeader key={col} fontSize="xs" fontFamily="mono" whiteSpace="nowrap">
                  {col}
                </Table.ColumnHeader>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {results.map((row, i) => (
              <Table.Row key={i}>
                {columns.map((col) => (
                  <Table.Cell
                    key={col}
                    fontSize="xs"
                    fontFamily="mono"
                    whiteSpace="nowrap"
                    maxW="300px"
                    overflow="hidden"
                    textOverflow="ellipsis"
                  >
                    {formatCellValue(row[col])}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Flex>
    </Flex>
  );
}

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// --- Execute Panel ---

function ExecutePanel({ instanceId }: { instanceId: string }) {
  const [mode, setMode] = useState<'Full' | 'PersistOnly' | 'DryRun'>('DryRun');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const queryClient = useQueryClient();

  const { mutate: trigger, isPending } = useMutation(
    trpc.emitter.triggerExecution.mutationOptions({
      onSuccess: (data) => {
        toaster.success({
          title: `Execution created (${mode})`,
          description: `Execution ${data.id} is pending.`,
        });
        setConfirmOpen(false);
        queryClient.invalidateQueries({
          queryKey: [['emitter', 'getInstance']],
        });
      },
      onError: (err) => {
        toaster.error({
          title: 'Failed to trigger execution',
          description: err.message,
        });
        setConfirmOpen(false);
      },
    })
  );

  const selectedMode = EXECUTION_MODES.find((m) => m.value === mode)!;

  const handleExecute = () => {
    if (mode === 'Full') {
      setConfirmOpen(true);
    } else {
      trigger({ instanceId, executionMode: mode });
    }
  };

  return (
    <Card>
      <Flex direction="column" gap={3}>
        <Text fontWeight="bold">Execute Instance</Text>
        <Flex gap={3} align="flex-end">
          <Flex direction="column" gap={1} flex={1}>
            <Text fontWeight="medium" fontSize="sm">
              Execution Mode
            </Text>
            <Select
              options={EXECUTION_MODES.map((m) => ({
                value: m.value,
                label: `${m.label} -- ${m.description}`,
              }))}
              value={[mode]}
              onValueChange={(details) =>
                setMode(details.value[0] as 'Full' | 'PersistOnly' | 'DryRun')
              }
            />
          </Flex>
          <Button variant={selectedMode.buttonVariant} onClick={handleExecute} loading={isPending}>
            {mode === 'Full'
              ? 'Execute (Full)'
              : mode === 'PersistOnly'
                ? 'Execute (Persist Only)'
                : 'Execute (Dry Run)'}
          </Button>
        </Flex>
        {mode === 'Full' && (
          <Text fontSize="xs" variant="danger">
            This will persist results AND emit events to downstream consumers.
          </Text>
        )}
        {mode === 'PersistOnly' && (
          <Text fontSize="xs" variant="warning">
            Results will be persisted but no events will be emitted.
          </Text>
        )}
      </Flex>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Confirm Full Execution"
        description="This will persist results AND emit events to downstream consumers. Are you sure?"
        confirmLabel="Execute (Full)"
        variant="primaryDestructive"
        onConfirm={() => trigger({ instanceId, executionMode: 'Full' })}
        onCancel={() => setConfirmOpen(false)}
        isLoading={isPending}
      />
    </Card>
  );
}

// --- Recent Executions ---

function RecentExecutions({
  executions,
}: {
  executions: Array<{
    id: string;
    status: string;
    scheduledAt: string | null;
    completedAt: string | null;
    totalResults: number | null;
    newResults: number | null;
    durationMs: number | null;
    executionMode: string;
  }>;
}) {
  const navigate = useNavigate();

  if (executions.length === 0) return null;

  return (
    <Card>
      <Flex direction="column" gap={3}>
        <Text fontWeight="bold">Recent Executions</Text>
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Status</Table.ColumnHeader>
              <Table.ColumnHeader>Mode</Table.ColumnHeader>
              <Table.ColumnHeader>Scheduled</Table.ColumnHeader>
              <Table.ColumnHeader>Results</Table.ColumnHeader>
              <Table.ColumnHeader>Duration</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {executions.map((exec) => (
              <Table.Row
                key={exec.id}
                cursor="pointer"
                _hover={{ bg: 'bg.muted' }}
                onClick={() => navigate({ to: `/emitters/executions/${exec.id}` })}
                data-testid={`recent-execution-row-${exec.id}`}
              >
                <Table.Cell>
                  <ExecutionStatusTag status={exec.status} />
                </Table.Cell>
                <Table.Cell>
                  <Tag
                    size="sm"
                    variant={EXECUTION_MODE_TAG_VARIANT[exec.executionMode] ?? 'default'}
                    label={exec.executionMode}
                  />
                </Table.Cell>
                <Table.Cell fontSize="xs">
                  {exec.scheduledAt ? new Date(exec.scheduledAt).toLocaleString() : '—'}
                </Table.Cell>
                <Table.Cell fontSize="xs">
                  {exec.totalResults != null
                    ? `${exec.newResults ?? 0} new / ${exec.totalResults} total`
                    : '—'}
                </Table.Cell>
                <Table.Cell fontSize="xs" fontFamily="mono">
                  {exec.durationMs != null ? formatDuration(exec.durationMs) : '—'}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Flex>
    </Card>
  );
}

function safeJsonParse(str: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(str);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    return {};
  } catch {
    return {};
  }
}

// --- Main Component ---

function InstanceDetail() {
  const { instanceId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);

  const {
    data: instance,
    isLoading,
    error: loadError,
  } = useQuery(trpc.emitter.getInstance.queryOptions({ id: instanceId }));

  const invalidateInstance = () => {
    queryClient.invalidateQueries({
      queryKey: [['emitter', 'getInstance']],
    });
    queryClient.invalidateQueries({
      queryKey: [['emitter', 'listInstances']],
    });
  };

  const { mutate: updateInstance, isPending: isUpdating } = useMutation(
    trpc.emitter.updateInstance.mutationOptions({
      onSuccess: () => {
        toaster.success({ title: 'Instance updated' });
        invalidateInstance();
      },
      onError: (err) => {
        toaster.error({
          title: 'Failed to update instance',
          description: err.message,
        });
      },
    })
  );

  const { mutate: pauseInstance, isPending: isPausing } = useMutation(
    trpc.emitter.pauseInstance.mutationOptions({
      onSuccess: () => {
        toaster.success({ title: 'Instance paused' });
        invalidateInstance();
      },
      onError: (err) => {
        toaster.error({ title: 'Failed to pause', description: err.message });
      },
    })
  );

  const { mutate: resumeInstance, isPending: isResuming } = useMutation(
    trpc.emitter.resumeInstance.mutationOptions({
      onSuccess: () => {
        toaster.success({ title: 'Instance resumed' });
        invalidateInstance();
      },
      onError: (err) => {
        toaster.error({ title: 'Failed to resume', description: err.message });
      },
    })
  );

  const { mutate: archiveInstance, isPending: isArchiving } = useMutation(
    trpc.emitter.archiveInstance.mutationOptions({
      onSuccess: () => {
        toaster.success({ title: 'Instance archived' });
        setArchiveConfirmOpen(false);
        invalidateInstance();
      },
      onError: (err) => {
        toaster.error({
          title: 'Failed to archive',
          description: err.message,
        });
        setArchiveConfirmOpen(false);
      },
    })
  );

  const { register, handleSubmit, watch, setValue, control } = useForm<InstanceFormValues>({
    values: instance
      ? {
          name: instance.name ?? '',
          parameters: JSON.stringify(instance.configuration?.parameters ?? {}, null, 2),
          scheduleType: instance.configuration?.scheduleType ?? '',
          scheduleCron: instance.configuration?.scheduleCron ?? '',
          dedupeStrategy: instance.configuration?.dedupeStrategy ?? '',
          dedupeWindowExecutions: instance.configuration?.dedupeWindowExecutions?.toString() ?? '',
          dedupeWindowDays: instance.configuration?.dedupeWindowDays?.toString() ?? '',
          dedupeWindowHours: instance.configuration?.dedupeWindowHours?.toString() ?? '',
          dataSourceNamespace: instance.configuration?.dataSourceNamespace ?? '',
          queryTimeoutSeconds: instance.configuration?.queryTimeoutSeconds?.toString() ?? '',
          staticData: instance.staticData ? JSON.stringify(instance.staticData, null, 2) : '',
        }
      : undefined,
  });

  if (isLoading) {
    return (
      <PageContainer>
        <Flex justify="center" align="center" py={16} width="100%">
          <Spinner size="lg" />
        </Flex>
      </PageContainer>
    );
  }

  if (loadError) {
    return (
      <PageContainer>
        <Flex direction="column" align="center" py={16} gap={3} width="100%">
          <Text variant="danger">Failed to load instance: {loadError.message}</Text>
          <Button variant="secondary" size="sm" onClick={() => navigate({ to: '/emitters' })}>
            Back to Instances
          </Button>
        </Flex>
      </PageContainer>
    );
  }

  if (!instance) return null;

  const scheduleType = watch('scheduleType');
  const isActionPending = isPausing || isResuming || isArchiving;

  // Mirror EnrollClinicDialog's lock semantics: a non-empty configurability
  // object locks every group not explicitly set to `true`; an empty or unset
  // object means fully configurable (legacy templates).
  // The pinned templateVersion snapshot, not template.configurability (the
  // freely-edited draft): the server validates against
  // existing.templateVersion.configurability, so reading the draft here renders
  // this control enabled while the mutation rejects with "Data source namespace
  // is locked by the template."
  const rawConfigurability = instance.templateVersion.configurability;
  const configurability =
    rawConfigurability &&
    typeof rawConfigurability === 'object' &&
    !Array.isArray(rawConfigurability) &&
    Object.keys(rawConfigurability).length > 0
      ? (rawConfigurability as Record<string, unknown>)
      : null;
  const namespaceConfigurable = !configurability || configurability.dataSourceNamespace === true;

  const onSubmit = (values: InstanceFormValues) => {
    let parsedParameters: Record<string, unknown> | undefined;
    let parsedStaticData: Record<string, unknown>[] | undefined;

    // Validate parameters JSON
    if (values.parameters) {
      try {
        parsedParameters = JSON.parse(values.parameters);
      } catch {
        toaster.error({
          title: 'Invalid parameters JSON',
          description: 'Please check your parameter JSON syntax.',
        });
        return;
      }
    }

    // Validate parameters against schema if available
    if (parsedParameters && instance.template.parameterSchema) {
      // A run merges these over the pinned version's parameters, so inherited values count.
      const validation = validateParameters(instance.template.parameterSchema, {
        ...parameterObject(instance.templateVersion.parameters),
        ...parsedParameters,
      });
      if (!validation.success) {
        toaster.error({
          title: 'Parameter validation failed',
          description: validation.errors.join('; '),
        });
        return;
      }
    }

    // Validate static data JSON. The server schema requires
    // `Array<Record<string, unknown>>`; surface a user-facing error here
    // rather than letting the tRPC call fail with a generic 400.
    if (values.staticData) {
      let raw: unknown;
      try {
        raw = JSON.parse(values.staticData);
      } catch {
        toaster.error({
          title: 'Invalid static data JSON',
          description: 'Please check your static data JSON syntax.',
        });
        return;
      }

      if (
        !Array.isArray(raw) ||
        !raw.every(
          (row): row is Record<string, unknown> =>
            typeof row === 'object' && row !== null && !Array.isArray(row)
        )
      ) {
        toaster.error({
          title: 'Invalid static data shape',
          description: 'Static data must be a JSON array of objects (e.g. `[{ "id": "1" }]`).',
        });
        return;
      }

      parsedStaticData = raw as Record<string, unknown>[];
    }

    // '' (the "Inherit template default" option) normalizes to null, which the
    // API treats as an explicit clear of the instance override. Send the field
    // only when it differs from the saved value: `undefined` means "leave
    // untouched", so an unchanged selection never trips the template's
    // configurability lock.
    const savedDataSourceNamespace = instance.configuration?.dataSourceNamespace ?? null;
    const selectedDataSourceNamespace =
      values.dataSourceNamespace === ''
        ? null
        : (values.dataSourceNamespace as DataSourceNamespace);
    const dataSourceNamespaceUpdate =
      selectedDataSourceNamespace === savedDataSourceNamespace
        ? undefined
        : selectedDataSourceNamespace;

    updateInstance({
      id: instanceId,
      name: values.name || undefined,
      staticData: parsedStaticData,
      configuration: {
        parameters: parsedParameters,
        scheduleType: values.scheduleType as 'Cron' | 'Once' | 'Manual',
        scheduleCron: values.scheduleCron || undefined,
        dedupeStrategy: values.dedupeStrategy as
          | 'None'
          | 'GlobalLifetime'
          | 'RollingWindow'
          | 'TimeWindow',
        dedupeWindowExecutions: values.dedupeWindowExecutions
          ? parseInt(values.dedupeWindowExecutions, 10)
          : undefined,
        dedupeWindowDays: values.dedupeWindowDays
          ? parseInt(values.dedupeWindowDays, 10)
          : undefined,
        // Explicit null clears the column. Hours takes precedence over days,
        // so leaving it undefined would make a once-set value permanent.
        dedupeWindowHours: values.dedupeWindowHours ? parseInt(values.dedupeWindowHours, 10) : null,
        dataSourceNamespace: dataSourceNamespaceUpdate,
        // Blank clears the override, so the template's limit (or the owner default) applies again.
        queryTimeoutSeconds: parseNumberInput(values.queryTimeoutSeconds) ?? null,
      },
    });
  };

  return (
    <PageContainer>
      <Flex direction="column" gap={4} p={8} width="100%">
        <Breadcrumbs
          items={[
            { label: 'Instances', to: '/emitters' },
            { label: instance.name ?? instance.template.name },
          ]}
        />

        <Flex justify="space-between" align="center">
          <Flex align="center" gap={3}>
            <Heading size="lg">{instance.name ?? instance.template.name}</Heading>
            <InstanceStatusTag status={instance.status} size="md" />
            <Tag variant="default" label={`v${instance.templateVersion.versionNumber}`} />
          </Flex>
          <Flex gap={2}>
            {instance.status === 'Active' && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => pauseInstance({ id: instanceId })}
                loading={isPausing}
                disabled={isActionPending}
              >
                Pause
              </Button>
            )}
            {instance.status === 'Paused' && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => resumeInstance({ id: instanceId })}
                loading={isResuming}
                disabled={isActionPending}
              >
                Resume
              </Button>
            )}
            {instance.status !== 'Archived' && (
              <Button
                size="sm"
                variant="secondaryDestructive"
                onClick={() => setArchiveConfirmOpen(true)}
                disabled={isActionPending}
              >
                Archive
              </Button>
            )}
          </Flex>
        </Flex>

        {/* Execute Panel */}
        <ExecutePanel instanceId={instanceId} />

        {/* Recent Executions */}
        <RecentExecutions
          executions={
            instance.executions as Array<{
              id: string;
              status: string;
              scheduledAt: string | null;
              completedAt: string | null;
              totalResults: number | null;
              newResults: number | null;
              durationMs: number | null;
              executionMode: string;
            }>
          }
        />

        <InstanceChainLimits
          instanceId={instanceId}
          rowLimit={instance.configuration?.rowLimit ?? null}
          watermarkWindowMaxMinutes={instance.configuration?.watermarkWindowMaxMinutes ?? null}
          lockReason={chunkLimitsLock({
            flagOn: instance.chunkLimitsEditable,
            archived: instance.status === 'Archived',
            instanceId,
            hasConfiguration: instance.configuration !== null,
          })}
        />

        {/* Parameter Test / Diff Panel */}
        <ParameterTestPanel
          instanceId={instanceId}
          currentParametersJson={JSON.stringify(instance.configuration?.parameters ?? {}, null, 2)}
        />

        {/* Instance Edit Form */}
        <Flex as="form" direction="column" gap={4} onSubmit={handleSubmit(onSubmit)}>
          <Card>
            <Flex direction="column" gap={3}>
              <Text fontWeight="bold">Instance Info</Text>
              <Flex gap={4}>
                <Flex direction="column" gap={1} flex={1}>
                  <Text fontWeight="medium" fontSize="sm">
                    Template
                  </Text>
                  <Text fontSize="sm" variant="subtle">
                    {instance.template.name}
                  </Text>
                </Flex>
                <Flex direction="column" gap={1} flex={1}>
                  <Text fontWeight="medium" fontSize="sm">
                    Owner
                  </Text>
                  <Text fontSize="sm" variant="subtle">
                    {instance.ownerType}: {instance.ownerId}
                  </Text>
                </Flex>
              </Flex>
              <Flex direction="column" gap={1}>
                <Text fontWeight="medium" fontSize="sm">
                  Name
                </Text>
                <TextInput {...register('name')} placeholder="Instance name (optional)" />
              </Flex>
            </Flex>
          </Card>

          <Card>
            <Flex direction="column" gap={4}>
              <Text fontWeight="bold">Parameters</Text>
              <Controller
                name="parameters"
                control={control}
                render={({ field }) => (
                  <InstanceParameterEditor
                    parameterSchema={instance.template.parameterSchema}
                    configurability={
                      (
                        instance.template.configurability as {
                          parameters?: Record<string, boolean>;
                        } | null
                      )?.parameters ?? {}
                    }
                    defaultParameters={
                      (
                        instance.template.defaultConfiguration as {
                          parameters?: Record<string, unknown>;
                        } | null
                      )?.parameters ?? {}
                    }
                    value={safeJsonParse(field.value)}
                    onChange={(obj) => field.onChange(JSON.stringify(obj, null, 2))}
                  />
                )}
              />
            </Flex>
          </Card>

          <Card>
            <Flex direction="column" gap={4}>
              <Text fontWeight="bold">Schedule</Text>
              <Flex gap={4}>
                <Flex direction="column" gap={1} flex={1}>
                  <Text fontWeight="medium" fontSize="sm">
                    Schedule Type
                  </Text>
                  <Select
                    options={SCHEDULE_TYPES}
                    value={[watch('scheduleType')]}
                    onValueChange={(details) =>
                      setValue('scheduleType', details.value[0] ?? '', {
                        shouldDirty: true,
                      })
                    }
                  />
                </Flex>
              </Flex>
              {scheduleType === 'Cron' && (
                <Flex direction="column" gap={1}>
                  <Text fontWeight="medium" fontSize="sm">
                    Cron Expression
                  </Text>
                  <TextInput
                    {...register('scheduleCron')}
                    placeholder="0 9 * * 1"
                    fontFamily="monospace"
                  />
                </Flex>
              )}
            </Flex>
          </Card>

          <Card>
            <Flex direction="column" gap={4}>
              <Text fontWeight="bold">Deduplication</Text>
              <Flex gap={4}>
                <Flex direction="column" gap={1} flex={1}>
                  <Text fontWeight="medium" fontSize="sm">
                    Strategy
                  </Text>
                  <Select
                    options={DEDUPE_STRATEGIES}
                    value={[watch('dedupeStrategy')]}
                    onValueChange={(details) =>
                      setValue('dedupeStrategy', details.value[0] ?? '', {
                        shouldDirty: true,
                      })
                    }
                  />
                </Flex>
                <Flex direction="column" gap={1} flex={1}>
                  <Text fontWeight="medium" fontSize="sm">
                    Window Executions
                  </Text>
                  <TextInput
                    {...register('dedupeWindowExecutions')}
                    type="number"
                    placeholder="Optional"
                  />
                </Flex>
                <Flex direction="column" gap={1} flex={1}>
                  <Text fontWeight="medium" fontSize="sm">
                    Window Days
                  </Text>
                  <TextInput
                    {...register('dedupeWindowDays')}
                    type="number"
                    placeholder="Optional"
                  />
                </Flex>
                <Flex direction="column" gap={1} flex={1}>
                  <Text fontWeight="medium" fontSize="sm">
                    Window Hours
                  </Text>
                  <TextInput
                    {...register('dedupeWindowHours')}
                    type="number"
                    placeholder="Optional"
                    data-testid="instance-dedupe-window-hours"
                  />
                </Flex>
              </Flex>
              <Text fontSize="xs" variant="subtle">
                Window Hours takes precedence over Window Days. With neither set, a Time Window
                strategy deduplicates nothing.
              </Text>
            </Flex>
          </Card>

          <Card opacity={namespaceConfigurable ? 1 : 0.6}>
            <Flex direction="column" gap={4}>
              <Flex align="center" gap={2}>
                <Text fontWeight="bold">Data Source Namespace</Text>
                {!namespaceConfigurable && (
                  <Flex align="center" gap={1}>
                    <Icon name="lock" size="sm" variant="subtle" />
                    <Text fontSize="xs" variant="subtle">
                      Locked by template
                    </Text>
                  </Flex>
                )}
              </Flex>
              <Flex direction="column" gap={1} flex={1} maxW="320px">
                <Text fontWeight="medium" fontSize="sm">
                  Namespace
                </Text>
                <Select
                  data-testid="emitter-instance-namespace-select"
                  disabled={!namespaceConfigurable}
                  options={DATA_SOURCE_NAMESPACES}
                  // watch() is undefined until `values` applies; [undefined] to [''] never re-syncs.
                  value={[watch('dataSourceNamespace') ?? '']}
                  onValueChange={(details) =>
                    setValue('dataSourceNamespace', details.value[0] ?? '', {
                      shouldDirty: true,
                    })
                  }
                />
                <Text fontSize="xs" variant="subtle">
                  Which Flow database (Owl or Yak) this emitter reads from. Flow connections only;
                  the template must allow overrides.
                </Text>
              </Flex>
            </Flex>
          </Card>

          <Card>
            <Flex direction="column" gap={4}>
              <Text fontWeight="bold">Query Time Limit</Text>
              <Flex direction="column" gap={1} flex={1} maxW="320px">
                <Text fontWeight="medium" fontSize="sm">
                  Seconds
                </Text>
                <TextInput
                  {...register('queryTimeoutSeconds')}
                  type="number"
                  min={1}
                  max={1800}
                  placeholder={
                    instance.templateVersion.queryTimeoutSeconds
                      ? `Template: ${instance.templateVersion.queryTimeoutSeconds}`
                      : 'Owner default'
                  }
                  data-testid="instance-query-timeout-seconds"
                />
                <Text fontSize="xs" variant="subtle">
                  How long the source query may run before the execution fails. Blank uses the
                  template&apos;s limit, else the owner default (180 s for System, 15 s otherwise).
                  Up to 1800 s.
                </Text>
              </Flex>
            </Flex>
          </Card>

          <Card>
            <Flex direction="column" gap={4}>
              <Text fontWeight="bold">Static Data</Text>
              <Flex direction="column" gap={1}>
                <Text fontWeight="medium" fontSize="sm">
                  Static Data (JSON, optional)
                </Text>
                <Controller
                  name="staticData"
                  control={control}
                  render={({ field }) => (
                    <CodeEditor
                      value={field.value}
                      onChange={field.onChange}
                      language="json"
                      minHeight="100px"
                    />
                  )}
                />
              </Flex>
            </Flex>
          </Card>

          <Flex gap={3} justify="flex-end">
            <Button variant="secondary" onClick={() => navigate({ to: '/emitters' })}>
              Cancel
            </Button>
            <Button type="submit" loading={isUpdating} disabled={instance.status === 'Archived'}>
              Save Changes
            </Button>
          </Flex>
        </Flex>
      </Flex>

      <ConfirmDialog
        isOpen={archiveConfirmOpen}
        title="Archive Instance"
        description={`Archive "${instance.name ?? instance.template.name}"? This instance will no longer execute and cannot be resumed.`}
        confirmLabel="Archive"
        variant="primaryDestructive"
        onConfirm={() => archiveInstance({ id: instanceId })}
        onCancel={() => setArchiveConfirmOpen(false)}
        isLoading={isArchiving}
      />
    </PageContainer>
  );
}
